/**
 * Score cumulé + étoiles par défi — localStorage versionné oc_v10_score
 * (migration douce depuis oc_v9_score/oc_v8_score : total et byGame repris, étoiles à zéro).
 * Namespacé par le PROFIL actif (`activeSuffix`) : chaque profil a son propre score.
 */
import type { Diff, GameId } from '@ordre-cache/shared';
import { NB_DEFIS } from '@ordre-cache/shared';
import { activeSuffix } from './profile';
import { scheduleSync } from './account';

interface ScoreData {
  total: number;
  byGame: Record<string, number>;
  /** Meilleures étoiles (0-3) par jeu → niveau → défi. */
  stars: Record<string, Record<number, number[]>>;
}

let score: ScoreData = { total: 0, byGame: {}, stars: {} };

export function loadScore(): void {
  const suf = activeSuffix();
  try {
    const raw = suf
      ? localStorage.getItem('oc_v10_score' + suf)
      : (localStorage.getItem('oc_v10_score') ??
        localStorage.getItem('oc_v9_score') ??
        localStorage.getItem('oc_v8_score'));
    const s = JSON.parse(raw ?? '{}') as Partial<ScoreData>;
    score = { total: s.total ?? 0, byGame: s.byGame ?? {}, stars: s.stars ?? {} };
  } catch {
    score = { total: 0, byGame: {}, stars: {} };
  }
}

export function saveScore(): void {
  try {
    localStorage.setItem('oc_v10_score' + activeSuffix(), JSON.stringify(score));
  } catch {
    /* ignore */
  }
  scheduleSync();
}

export function addScore(gid: GameId, pts: number): void {
  score.total += pts;
  score.byGame[gid] = (score.byGame[gid] ?? 0) + pts;
  saveScore();
}

export function totalScore(): number {
  return score.total;
}

export function gameScore(gid: GameId): number {
  return score.byGame[gid] ?? 0;
}

/** Meilleures étoiles enregistrées pour (jeu, niveau, défi) — 0 si jamais réussi. */
export function bestStars(gid: GameId, diff: Diff, defi: number): number {
  return score.stars[gid]?.[diff]?.[defi] ?? 0;
}

/** Enregistre les étoiles si meilleures que l'historique. Renvoie l'ancien meilleur. */
export function recordStars(gid: GameId, diff: Diff, defi: number, stars: number): number {
  const g = (score.stars[gid] ??= {});
  const arr = (g[diff] ??= new Array(NB_DEFIS).fill(0) as number[]);
  const prev = arr[defi] ?? 0;
  if (stars > prev) {
    arr[defi] = stars;
    saveScore();
  }
  return prev;
}

/** Somme des étoiles d'un jeu au niveau donné (max 18). */
export function gameStars(gid: GameId, diff: Diff): number {
  const arr = score.stars[gid]?.[diff];
  return arr ? arr.reduce((a, b) => a + b, 0) : 0;
}

/** Somme des étoiles tous jeux au niveau donné (max 144). */
export function totalStars(diff: Diff): number {
  let t = 0;
  for (const gid of Object.keys(score.stars)) {
    const arr = score.stars[gid]?.[diff];
    if (arr) t += arr.reduce((a, b) => a + b, 0);
  }
  return t;
}
